import {
  Stack, Title, Card, Group, Button, Table, Text, Skeleton, ActionIcon, Badge, Modal, TextInput, MultiSelect,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useCreate, useList, useRemove, useUpdate } from '../../api/hooks/useCrud.ts';
import { useAdminUsers } from '../../api/hooks/useUsers.ts';
import { HttpError } from '../../api/client.ts';
import type { Role, UserMini } from '../../api/schemas/entities.ts';

/**
 * Roles admin page. Each row shows the role's current member badges; the
 * edit modal lets you rename the role AND add/remove users by re-syncing
 * the role_users join (the server PUT /api/roles handler replaces the
 * member set with whatever the payload's `users` array contains).
 */
export function RolesPage() {
  const list = useList<Role>('roles');
  const create = useCreate<Role>('roles');
  const update = useUpdate<Role>('roles');
  const remove = useRemove('roles');
  const users = useAdminUsers();
  const [editing, setEditing] = useState<Role | 'new' | null>(null);

  function onDelete(id: number) {
    modals.openConfirmModal({
      title: 'Delete role',
      children: <Text size="sm">This unlinks every user from the role and deletes it. Continue?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        remove.mutate(id, {
          onSuccess: () => notifications.show({ message: 'Deleted', color: 'green' }),
          onError: err => notifications.show({ message: errorMessage(err), color: 'red' }),
        });
      },
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Roles</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>New</Button>
      </Group>

      <Card withBorder padding={0} radius="md">
        {list.isPending ? (
          <Stack gap="xs" p="md">{[1,2,3,4,5].map(i => <Skeleton key={i} h={24} />)}</Stack>
        ) : list.isError ? (
          <Text p="md" c="red">{errorMessage(list.error)}</Text>
        ) : (
          <Table.ScrollContainer minWidth={500}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Users</Table.Th>
                  <Table.Th style={{ width: 80 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data ?? []).map(r => (
                  <Table.Tr key={String(r.id)}>
                    <Table.Td>{r.id}</Table.Td>
                    <Table.Td>{r.name ?? ''}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        {(r.users ?? []).slice(0, 6).map(u => (
                          <Badge key={u.id} variant="light" size="sm">{u.login ?? u.email ?? u.id}</Badge>
                        ))}
                        {(r.users ?? []).length > 6 && (
                          <Text size="sm" c="dimmed">+{(r.users ?? []).length - 6}</Text>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end">
                        <ActionIcon variant="subtle" onClick={() => setEditing(r)} aria-label="Edit">
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle" color="red"
                          onClick={() => r.id != null && onDelete(r.id)}
                          aria-label="Delete"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      {editing !== null && (
        <RoleEditModal
          initial={editing === 'new' ? { name: '', users: [] } : editing}
          isNew={editing === 'new'}
          availableUsers={users.data ?? []}
          usersLoading={users.isPending}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            try {
              if (editing === 'new') {
                await create.mutateAsync(values);
                notifications.show({ message: 'Created', color: 'green' });
              } else {
                await update.mutateAsync(values as Role);
                notifications.show({ message: 'Updated', color: 'green' });
              }
              setEditing(null);
            } catch (err) {
              notifications.show({ message: errorMessage(err), color: 'red' });
            }
          }}
        />
      )}
    </Stack>
  );
}

interface RoleEditModalProps {
  initial: Partial<Role>;
  isNew: boolean;
  availableUsers: { id: number; login: string; email?: string | null }[];
  usersLoading: boolean;
  onClose(): void;
  onSubmit(values: Partial<Role>): Promise<void>;
}

function RoleEditModal({ initial, isNew, availableUsers, usersLoading, onClose, onSubmit }: RoleEditModalProps) {
  const initialUserIds = useMemo(
    () => (initial.users ?? []).map(u => String(u.id)),
    [initial.users],
  );

  const form = useForm({
    initialValues: {
      ...initial,
      name: initial.name ?? '',
      userIds: initialUserIds,
    },
  });
  const [submitting, setSubmitting] = useState(false);

  const userOptions = useMemo(
    () =>
      availableUsers.map(u => ({
        value: String(u.id),
        label: u.email ?? u.login,
      })),
    [availableUsers],
  );

  return (
    <Modal opened onClose={onClose} title={isNew ? 'New role' : `Edit role #${initial.id}`} centered size="lg">
      <form
        onSubmit={form.onSubmit(async (v) => {
          setSubmitting(true);
          // Translate selected user ids back into the role_users payload shape.
          const selectedUsers: UserMini[] = (v.userIds ?? [])
            .map(id => availableUsers.find(u => String(u.id) === id))
            .filter((u): u is { id: number; login: string; email?: string | null } => u != null)
            .map(u => ({
              id: u.id,
              login: u.login,
              email: u.email ?? null,
              firstName: null,
              lastName: null,
            }));
          // Only send `users` when the full user list is available. If it
          // failed to load, availableUsers is empty and every id maps to
          // nothing — sending that would wipe the role's membership. Omitting
          // `users` makes the server preserve the existing members (a rename
          // still applies).
          const canManageMembers = availableUsers.length > 0;
          await onSubmit({
            id: v.id,
            name: v.name,
            ...(canManageMembers ? { users: selectedUsers } : {}),
          });
          setSubmitting(false);
        })}
      >
        <Stack gap="sm">
          <TextInput label="Name" required {...form.getInputProps('name')} />
          {!usersLoading && availableUsers.length === 0 && (
            <Text size="sm" c="orange">
              User list unavailable — you can rename the role, but membership can't be edited and will be left unchanged.
            </Text>
          )}
          <MultiSelect
            label="Members"
            description="Users assigned to this role."
            placeholder={usersLoading ? 'Loading users…' : 'Pick users'}
            data={userOptions}
            value={form.values.userIds ?? []}
            onChange={ids => form.setFieldValue('userIds', ids)}
            searchable
            clearable
            hidePickedOptions
            disabled={usersLoading || availableUsers.length === 0}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={submitting}>Save</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof HttpError) {
    if (typeof err.body === 'object' && err.body && 'detail' in err.body) {
      return String((err.body as { detail: unknown }).detail);
    }
    return `${err.status} on ${err.url}`;
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}
